import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import EnvelopeHero from "@/components/invite/EnvelopeHero";
import Countdown from "@/components/invite/Countdown";
import Marquee from "@/components/invite/Marquee";
import LocationSection from "@/components/invite/LocationSection";
import RsvpSection from "@/components/invite/RsvpSection";
import { Reveal, FadeUp } from "@/components/invite/Reveal";
import { mediaUrl } from "@/lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Foto de reserva mientras los novios no suban la suya desde el panel.
const COUPLE_FALLBACK =
  "https://images.unsplash.com/photo-1638763757006-94414ab2ccf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHx3ZWRkaW5nJTIwY291cGxlJTIwZWxlZ2FudCUyMG1pbmltYWx8ZW58MHx8fHwxNzg4MTk0NTg5fDA&ixlib=rb-4.1.0&q=85";


function QuoteStrip({ content }) {
  const t = content.texts;
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [500, 1600], [40, -60]);
  return (
    <section className="bg-sand px-6 sm:px-12 py-24 sm:py-32 overflow-hidden" data-testid="quote-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <Reveal>
            <span className="overline-label text-bronze">{t.quote_overline}</span>
          </Reveal>
          <Reveal delay={0.1} className="mt-6">
            <h2 className="font-serif text-4xl sm:text-6xl font-light tracking-tighter text-ink leading-[1.05]">
              {t.quote_title} <span className="italic text-olive">{t.quote_accent}</span>
            </h2>
          </Reveal>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-md text-sm sm:text-base text-taupe leading-relaxed">
              {t.quote_body}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="mt-10 font-serif italic text-2xl sm:text-3xl text-bronze">
              {`“${t.quote_cite}”`}
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="md:col-span-4 md:col-start-9">
          <motion.div style={{ y: imgY }} className="border border-nude p-3 bg-cream">
            <img
              src={mediaUrl(content.photos.couple, COUPLE_FALLBACK)}
              alt="Leticia y Nick"
              className="aspect-[3/4] max-h-[80vh] w-full object-cover"
              data-testid="couple-image"
            />
          </motion.div>
        </FadeUp>
      </div>
    </section>
  );
}

function CountdownSection({ wedding }) {
  return (
    <section className="bg-cream px-6 py-24 sm:py-32 text-center" data-testid="countdown-section">
      <Reveal>
        <span className="overline-label text-bronze">Prepárate</span>
      </Reveal>
      <Reveal delay={0.1} className="mt-6">
        <h2 className="font-serif text-3xl sm:text-5xl font-light tracking-tight text-ink">
          La cuenta regresiva ya comienza…
        </h2>
      </Reveal>
      <FadeUp delay={0.2} className="mx-auto mt-14 max-w-3xl">
        <Countdown dateIso={wedding.date_iso} />
      </FadeUp>
    </section>
  );
}

function Footer({ wedding }) {
  return (
    <footer className="bg-cream border-t border-nude px-6 py-16 text-center" data-testid="invite-footer">
      <p className="font-serif italic text-4xl text-olive">L &amp; N</p>
      <p className="overline-label text-bronze mt-6">{wedding.date_label}</p>
      <p className="mt-8 text-xs text-taupe">¡Te esperamos!</p>
    </footer>
  );
}

export default function InvitePage() {
  const { code } = useParams();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/invite/${code}`)
      .then((res) => {
        if (cancelled) return;
        setInvite(res.data);
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Los novios pueden dejar la invitación reducida al sobre desde el panel.
  const onlyEnvelope = Boolean(invite?.content?.options?.only_envelope);

  useEffect(() => {
    if (status !== "ready" || onlyEnvelope) return;
    const lenis = new Lenis({ lerp: 0.08 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [status, onlyEnvelope]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="invite-loading">
        <p className="font-serif italic text-3xl text-bronze animate-pulse">L &amp; N</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-6 text-center" data-testid="invite-not-found">
        <p className="overline-label text-bronze">Leticia &amp; Nick</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-6 tracking-tight">
          Invitación no encontrada
        </h1>
        <p className="mt-6 max-w-md text-sm text-taupe leading-relaxed">
          El enlace que ingresaste no corresponde a ninguna invitación. Revisa que esté
          escrito correctamente.
        </p>
      </div>
    );
  }

  return (
    <main className="bg-cream" data-testid="invite-page">
      <EnvelopeHero invite={invite} standalone={onlyEnvelope} />
      {!onlyEnvelope && (
        <>
          <QuoteStrip content={invite.content} />
          <Marquee wedding={invite.wedding} content={invite.content} />
          <CountdownSection wedding={invite.wedding} />
          <LocationSection wedding={invite.wedding} content={invite.content} />
          <RsvpSection invite={invite} />
          <Footer wedding={invite.wedding} />
        </>
      )}
    </main>
  );
}
